import { fmhAPI } from 'services/Http';

export const PRESCRIBER_DEACTIVATION_MESSAGE_HEADER = 'X-Prescriber-Deactivation-Message';

export const DEFAULT_PRESCRIBER_DEACTIVATED_MESSAGE = 'Prescriber NPI is deactivated.';

/**
 * Load a prescriber by NPI, handling deactivated (204 / deactivation header) responses
 * without attempting to parse an empty JSON body.
 *
 * @returns {Promise<
 *   | { status: 'active', prescriber: object }
 *   | { status: 'deactivated', npi: string, message: string, prescriber: object | null }
 *   | { status: 'not_found', npi: string }
 * >}
 */
export const fetchPrescriberByNpi = async (npi) => {
    try {
        const response = await fmhAPI.base.url(`/reference/prescribers/${npi}`).get().res();
        const deactivationMessage = response.headers.get(PRESCRIBER_DEACTIVATION_MESSAGE_HEADER);

        if (response.status === 204) {
            return {
                status: 'deactivated',
                npi,
                message: deactivationMessage ?? DEFAULT_PRESCRIBER_DEACTIVATED_MESSAGE,
                prescriber: null,
            };
        }

        const responseText = await response.text();
        const prescriber = responseText ? JSON.parse(responseText) : null;

        if (deactivationMessage) {
            return {
                status: 'deactivated',
                npi,
                message: deactivationMessage,
                prescriber,
            };
        }

        return { status: 'active', prescriber };
    } catch (error) {
        if (error?.status === 404) {
            return { status: 'not_found', npi };
        }
        throw error;
    }
};

/** Remove an NPI from the prescriber deactivation list. */
export const reactivatePrescriber = async (npi) => {
    const response = await fmhAPI.base
        .url(`/reference/deactivated_prescriber/${npi}`)
        .delete()
        .res();

    if (!response.ok) {
        const errorText = await response.text();
        const error = new Error(errorText || `Request failed with status ${response.status}`);
        error.status = response.status;
        throw error;
    }

    return (
        response.headers.get(PRESCRIBER_DEACTIVATION_MESSAGE_HEADER) ??
        'Prescriber deactivation removed.'
    );
};
